import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { adminApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  MetricCard,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Textarea,
  ViewButton,
  useToast,
} from '@/components/ui';
import { RemoveListingModal } from '@/features/admin/RemoveListingModal';
import { INTERNSHIP_TONES, humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

// Every status a listing can be in. The endpoint has always accepted the
// `statuses` filter for all six; the console showed three, so a draft, a closed
// listing or an archived one was invisible here -- including the ones an admin
// closed themselves.
const TABS = [
  { key: 'PENDING_APPROVAL', label: 'Pending' },
  { key: 'PUBLISHED', label: 'Published' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'FILLED', label: 'Filled' },
  { key: 'DRAFT', label: 'Drafts' },
  { key: 'ARCHIVED', label: 'Archived' },
];

// Approve/reject decide whether a listing goes up. Taking one down is the other
// decision and applies to a listing that is already out there.
const TAKE_DOWN_STATUSES = new Set(['PUBLISHED', 'PENDING_APPROVAL']);
// Removing as fraudulent also reaches a listing already taken down: closing
// it may have been the first step while the complaint was checked.
const REMOVE_STATUSES = new Set(['PUBLISHED', 'PENDING_APPROVAL', 'CLOSED', 'FILLED']);

const PAGE_SIZE = 20;

/** The four numbers worth seeing before choosing a tab. */
const STATS = [
  { key: 'PENDING_APPROVAL', label: 'Pending approval', icon: 'clockOutline', tone: 'warning' },
  { key: 'PUBLISHED', label: 'Published', icon: 'check', tone: 'success' },
  { key: 'REJECTED', label: 'Rejected', icon: 'close', tone: 'danger' },
  { key: 'DRAFT', label: 'Drafts', icon: 'pencil', tone: 'neutral' },
];

const WORK_MODES = { REMOTE: 'Remote', HYBRID: 'Hybrid', ONSITE: 'On site' };

/** The stripe down each box's left edge, in the status's own colour. */
const STATUS_ACCENTS = {
  PENDING_APPROVAL: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'danger',
  CLOSED: 'neutral',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

export function AdminInternshipQueuePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('PENDING_APPROVAL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const term = search.trim();
  const searchParam = term ? { q: term } : {};
  const [rejecting, setRejecting] = useState(null);
  const [takingDown, setTakingDown] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [reason, setReason] = useState('');

  const { data, dataUpdatedAt, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-internships', status, page, term],
    queryFn: () =>
      adminApi.listInternshipQueue({
        page,
        page_size: PAGE_SIZE,
        statuses: status,
        ...searchParam,
      }),
    // Switching tab or page keeps the queue on screen instead of blanking it,
    // and always asks again: a company may have submitted or withdrawn a
    // listing since this tab was last open.
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  // One count per status, read as the total of a one-row page: the list
  // endpoint already knows it, so no new API is needed. With a search typed,
  // the counts follow it -- so the tabs say where the matches are.
  const countQueries = useQueries({
    queries: TABS.map((tab) => ({
      queryKey: ['admin-internships', 'count', tab.key, term],
      queryFn: () =>
        adminApi.listInternshipQueue({
          page: 1,
          page_size: 1,
          statuses: tab.key,
          ...searchParam,
        }),
      placeholderData: keepPreviousData,
    })),
  });

  // The counts follow every fresh read of the list. Cached on their own, a
  // listing that moved from Drafts to Pending left the tabs saying the
  // opposite of the list under them until a full reload.
  useEffect(() => {
    if (!dataUpdatedAt) return;
    void queryClient.invalidateQueries({ queryKey: ['admin-internships', 'count'] });
  }, [dataUpdatedAt, queryClient]);
  const counts = Object.fromEntries(
    TABS.map((tab, index) => [tab.key, countQueries[index].data?.meta?.total_items]),
  );

  function select(key) {
    setStatus(key);
    setPage(1);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-internships'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
  }

  const approve = useMutation({
    mutationFn: (id) => adminApi.approveInternship(id),
    onSuccess: (internship) => {
      toast.success(`"${internship.title}" is live`);
      invalidate();
    },
    onError: (approveError) =>
      toast.error(errorMessage(approveError, 'Could not approve the internship')),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason: text }) => adminApi.rejectInternship(id, text),
    onSuccess: () => {
      toast.success('Internship rejected');
      invalidate();
      setRejecting(null);
      setReason('');
    },
    onError: (rejectError) =>
      toast.error(errorMessage(rejectError, 'Could not reject the internship')),
  });

  const takeDown = useMutation({
    mutationFn: ({ id, reason: text }) => adminApi.closeInternship(id, text),
    onSuccess: () => {
      toast.success('Listing taken down');
      invalidate();
      setTakingDown(null);
      setReason('');
    },
    onError: (closeError) =>
      toast.error(errorMessage(closeError, 'Could not take the listing down')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Internships"
        description="Every listing a company publishes waits here for approval before students can see it. Every listing on the platform is here, and a live one can be taken down."
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STATS.map((stat) => (
          <MetricCard
            key={stat.key}
            label={stat.label}
            value={counts[stat.key] ?? '–'}
            icon={stat.icon}
            tone={stat.tone}
            active={status === stat.key}
            onClick={() => select(stat.key)}
          />
        ))}
      </div>

      {/* Loading and errors render inside the card, below the tabs, so
          switching tab never unmounts the rail the admin is using. */}
      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS.map((tab) => ({ ...tab, count: counts[tab.key] }))}
            value={status}
            onChange={select}
            label="Internship status"
          />
        }
        toolbarEnd={
          <SearchInput
            label="Search internships"
            hideLabel
            size="sm"
            className="w-full sm:w-72"
            placeholder="Title, company or location"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading the internship queue…" />
          ) : rows.length === 0 ? (
            term ? (
              <EmptyState
                icon={<Icon name="search" size={22} />}
                title="No matching internships"
                description={`Nothing in this tab matches "${term}". The counts on the tabs show where matches are.`}
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setPage(1);
                    }}
                  >
                    Clear search
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="Nothing to review"
                description="No internships in this state right now."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => {
            const pending = row.status === 'PENDING_APPROVAL';
            return (
              <BoxRow
                key={row.id}
                accent={STATUS_ACCENTS[row.status] ?? 'neutral'}
                leading={<BoxAvatar name={row.company_name} />}
                title={row.title}
                to={paths.admin.internshipDetail(row.id)}
                badges={
                  <Badge dot tone={INTERNSHIP_TONES[row.status] ?? 'neutral'}>
                    {humanizeStatus(row.status)}
                  </Badge>
                }
                subtitle={
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate">{row.company_name}</span>
                    {row.company_is_verified && (
                      <span title="Verified company" className="text-success-600 shrink-0">
                        <Icon name="shieldCheck" size={15} />
                        <span className="sr-only">Verified company</span>
                      </span>
                    )}
                  </span>
                }
                facts={[
                  row.location && { icon: 'home', text: row.location },
                  row.work_mode && {
                    icon: 'briefcase',
                    text: WORK_MODES[row.work_mode] ?? row.work_mode,
                  },
                  {
                    icon: 'users',
                    text: `${row.positions} ${row.positions === 1 ? 'position' : 'positions'}`,
                  },
                  { icon: 'calendar', text: `Submitted ${formatDate(row.updated_at)}` },
                ]}
                description={row.description}
                // The review decision sits under the line; the moderation
                // actions that end a listing sit in the corner.
                // A pending listing's main action is the decision, so View
                // steps back to secondary and Approve sits last.
                actions={
                  <CardActions>
                    <ViewButton
                      to={paths.admin.internshipDetail(row.id)}
                      title={`Open "${row.title}"`}
                      variant={pending ? 'secondary' : 'tintBrand'}
                    />
                    {pending && (
                      <>
                        <Button
                          variant="tintDanger"
                          size="sm"
                          title={`Reject "${row.title}"`}
                          onClick={() => setRejecting(row)}
                        >
                          <Icon name="close" size={14} />
                          Reject
                        </Button>
                        <Button
                          variant="tintPositive"
                          size="sm"
                          title={
                            row.company_is_verified
                              ? `Approve "${row.title}" and publish it`
                              : `Verify ${row.company_name} first`
                          }
                          isLoading={approve.isPending && approve.variables === row.id}
                          disabled={!row.company_is_verified}
                          onClick={() => approve.mutate(row.id)}
                        >
                          <Icon name="check" size={14} />
                          Approve
                        </Button>
                      </>
                    )}
                  </CardActions>
                }
                corner={
                  <>
                    {TAKE_DOWN_STATUSES.has(row.status) && (
                      <Button
                        variant="ghostDanger"
                        size="sm"
                        title={`Take "${row.title}" down`}
                        onClick={() => setTakingDown(row)}
                      >
                        <Icon name="archive" size={14} />
                        Take down
                      </Button>
                    )}
                    {REMOVE_STATUSES.has(row.status) && (
                      <Button
                        variant="ghostDanger"
                        size="sm"
                        title={`Remove "${row.title}" as fraudulent`}
                        onClick={() => setRemoving(row)}
                      >
                        <Icon name="flag" size={14} />
                        Remove as fraudulent
                      </Button>
                    )}
                  </>
                }
              />
            );
          })}
        </BoxList>
      </ListCard>

      <RemoveListingModal
        internship={removing}
        onClose={() => setRemoving(null)}
        onRemoved={invalidate}
      />

      <Modal
        open={Boolean(takingDown)}
        title={`Take "${takingDown?.title}" down`}
        description="It stops being findable straight away and the company is told why. Applications already made are untouched, and the listing is not deleted."
        onClose={() => setTakingDown(null)}
      >
        <div className="space-y-4">
          <Textarea
            label="Reason"
            required
            rows={4}
            placeholder="The listing asks applicants to pay a deposit before an interview."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setTakingDown(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={takeDown.isPending}
              onClick={() => takeDown.mutate({ id: takingDown.id, reason: reason.trim() })}
            >
              Take it down
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(rejecting)}
        title={`Reject "${rejecting?.title}"`}
        description="The company sees this reason and can fix and resubmit."
        onClose={() => setRejecting(null)}
      >
        <div className="space-y-4">
          <Textarea
            label="Reason"
            required
            rows={4}
            placeholder="The description does not explain what the intern will actually do."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={reject.isPending}
              onClick={() => reject.mutate({ id: rejecting.id, reason: reason.trim() })}
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
